import React, { useEffect, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DateCalendar, DateField, PickersDay } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

export interface DateRange {
  from: string | null;
  to: string | null;
}

interface DateRangePickerProps {
  label: string;
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  dateFormat: string;
  disabled?: boolean;
  showTitleOnCustom?: boolean;
  notSetText?: string;
  customRanges?: Record<string, { from: Dayjs; to: Dayjs }>;
}

const defaultPredefinedRanges = {
  Today: { from: dayjs().startOf("day"), to: dayjs().endOf("day") },
  Yesterday: { from: dayjs().subtract(1, "day").startOf("day"), to: dayjs().subtract(1, "day").endOf("day") },
  "Last 7 Days": { from: dayjs().subtract(6, "day").startOf("day"), to: dayjs().endOf("day") },
  "Last 30 Days": { from: dayjs().subtract(29, "day").startOf("day"), to: dayjs().endOf("day") },
  "This Month": { from: dayjs().startOf("month"), to: dayjs().endOf("month") },
  "Last Month": { from: dayjs().subtract(1, "month").startOf("month"), to: dayjs().subtract(1, "month").endOf("month") }
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  value,
  onChange,
  dateFormat,
  disabled = false,
  showTitleOnCustom = true,
  notSetText = "∞",
  customRanges
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tempDateRange, setTempDateRange] = useState<{ from: Dayjs | null; to: Dayjs | null }>({
    from: value?.from ? dayjs(value.from) : null,
    to: value?.to ? dayjs(value.to) : null
  });

  const [tempDateRangeCalendars, setTempDateRangeCalendars] = useState<{ from: Dayjs | null; to: Dayjs | null }>({
    from: value?.from ? dayjs(value.from) : null,
    to: value?.to ? dayjs(value.to) : null
  });

  const [selectedRange, setSelectedRange] = useState<string | null>(null);

  useEffect(() => {
    setTempDateRange({
      from: value?.from ? dayjs(value.from) : null,
      to: value?.to ? dayjs(value.to) : null
    });
    setTempDateRangeCalendars({
      from: value?.from ? dayjs(value.from) : null,
      to: value?.to ? dayjs(value.to) : null
    });
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      const newFrom = value?.from ? dayjs(value.from) : null;
      const newTo = value?.to ? dayjs(value.to) : null;
      const ranges = customRanges || defaultPredefinedRanges;
      if (!newFrom && !newTo) {
        const todayRange = ranges["Today"];
        setTempDateRange({ from: todayRange.from, to: todayRange.to });
        setTempDateRangeCalendars({ from: todayRange.from, to: todayRange.to });
        setSelectedRange("Today");
      } else {
        setTempDateRange({
          from: newFrom,
          to: newTo
        });
        setTempDateRangeCalendars({
          from: newFrom,
          to: newTo
        });
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTempDateChangeCalendars = (date: Dayjs | null, type: "from" | "to") => {
    setTempDateRangeCalendars((prev) => {
      if (prev[type] !== date) {
        return {
          ...prev,
          [type]: date
        };
      }
      return prev;
    });
  };

  const handleApply = () => {
    if (selectedRange === "Custom Range" || (!tempDateRangeCalendars.from && !tempDateRangeCalendars.to)) {
      setTempDateRange({
        from: tempDateRangeCalendars.from,
        to: tempDateRangeCalendars.to
      });
      onChange({
        from: tempDateRangeCalendars.from?.toISOString() || null,
        to: tempDateRangeCalendars.to?.toISOString() || null
      });
    }
    handleClose();
  };

  const handlePredefinedRangeChange = (range: string) => {
    const ranges = customRanges || defaultPredefinedRanges;
    const selected = ranges[range as keyof typeof ranges];
    if (selected) {
      setTempDateRange({ from: selected.from, to: selected.to });
      onChange({ from: selected.from.toISOString(), to: selected.to.toISOString() });
      setSelectedRange(range);
      handleClose();
    } else if (range === "Custom Range") {
      if (!tempDateRange.from && !tempDateRange.to) {
        const todayRange = ranges["Today"];
        setTempDateRangeCalendars({ from: todayRange.from, to: todayRange.to });
      }
      setSelectedRange(range);
    } else {
      console.error(`Range "${range}" is not defined in ranges.`);
    }
  };

  const clearDate = (type: "from" | "to") => {
    setTempDateRangeCalendars((prev) => ({
      ...prev,
      [type]: null
    }));
  };

  const clearDateRange = (event: React.MouseEvent) => {
    event.stopPropagation();
    setTempDateRange({ from: null, to: null });
    setTempDateRangeCalendars({ from: null, to: null });
    onChange({ from: null, to: null });
    setSelectedRange(null);
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Stack direction="row" alignItems="center" spacing={1} onClick={handleClick} sx={{ cursor: "pointer" }}>
        <TextField
          fullWidth
          value={
            !tempDateRange.from && !tempDateRange.to
              ? ""
              : `${tempDateRange.from?.format(dateFormat) || notSetText} - ${
                  tempDateRange.to?.format(dateFormat) || notSetText
                }`
          }
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                {(tempDateRange.from || tempDateRange.to) && (
                  <IconButton onClick={clearDateRange}>
                    <ClearIcon />
                  </IconButton>
                )}
                <DateRangeIcon
                  sx={{
                    color: "black"
                  }}
                />
              </InputAdornment>
            )
          }}
          disabled={disabled}
          sx={{
            flexGrow: 1,
            pointerEvents: disabled ? "none" : "auto",
            cursor: disabled ? "not-allowed" : "pointer"
          }}
          label={label}
        />
      </Stack>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "none",
            borderRadius: "2px",
            border: "1px solid #E0E0E0"
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            backgroundColor: "whitesmoke",
            minWidth: selectedRange === "Custom Range" ? 600 : "auto"
          }}
        >
          <List sx={{ minWidth: 150, backgroundColor: "transparent" }}>
            {Object.keys(customRanges || defaultPredefinedRanges).map((range) => (
              <ListItem
                component="button"
                key={range}
                onClick={() => handlePredefinedRangeChange(range)}
                sx={{
                  padding: "0 15px",
                  border: "none",
                  backgroundColor: selectedRange === range ? "rgba(0, 0, 0, 0.08)" : "transparent",
                  "&:hover": {
                    backgroundColor: selectedRange !== range ? "rgba(0, 0, 0, 0.04)" : "rgba(0, 0, 0, 0.08)",
                    cursor: "pointer"
                  }
                }}
              >
                <ListItemText primary={range} />
              </ListItem>
            ))}
            <ListItem
              component="button"
              onClick={() => handlePredefinedRangeChange("Custom Range")}
              sx={{
                padding: "0 15px",
                border: "none",
                backgroundColor: selectedRange === "Custom Range" ? "rgba(0, 0, 0, 0.08)" : "transparent",
                "&:hover": {
                  backgroundColor: selectedRange !== "Custom Range" ? "rgba(0, 0, 0, 0.04)" : "rgba(0, 0, 0, 0.08)",
                  cursor: "pointer"
                }
              }}
            >
              <ListItemText primary="Custom Range" />
            </ListItem>
          </List>
          {selectedRange === "Custom Range" && (
            <Box sx={{ p: "15px", backgroundColor: "white" }}>
              {showTitleOnCustom && (
                <>
                  <Stack direction="row" spacing={2}>
                    <Typography variant="subtitle2">Select dates range for {label}</Typography>
                  </Stack>
                  <Divider sx={{ height: "1px", color: "black", marginBottom: "15px" }} />
                </>
              )}
              <Stack direction="row" spacing={2}>
                <Box>
                  <DateField
                    label="From"
                    fullWidth
                    value={tempDateRangeCalendars.from}
                    onChange={(date) => handleTempDateChangeCalendars(date, "from")}
                    onBlur={() => handleTempDateChangeCalendars(tempDateRangeCalendars.from, "from")}
                    format={dateFormat}
                    sx={{ mb: 1 }}
                    maxDate={tempDateRangeCalendars.to || undefined}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => clearDate("from")}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <DateCalendar
                    value={tempDateRangeCalendars.from}
                    onChange={(date) => handleTempDateChangeCalendars(date, "from")}
                    maxDate={tempDateRangeCalendars.to || undefined}
                    disableHighlightToday={true}
                    slots={{
                      day: PickersDay
                    }}
                    slotProps={{
                      day: (dayProps) => ({
                        sx: {
                          backgroundColor:
                            tempDateRangeCalendars.from &&
                            tempDateRangeCalendars.to &&
                            dayjs(dayProps.day).isBetween(
                              tempDateRangeCalendars.from,
                              tempDateRangeCalendars.to,
                              null,
                              "[]"
                            ) &&
                            !dayjs(dayProps.day).isSame(tempDateRangeCalendars.from, "day")
                              ? "lightgray"
                              : undefined
                        }
                      })
                    }}
                  />
                </Box>
                <Box>
                  <DateField
                    label="To"
                    fullWidth
                    value={tempDateRangeCalendars.to}
                    onChange={(date) => handleTempDateChangeCalendars(date, "to")}
                    onBlur={() => handleTempDateChangeCalendars(tempDateRangeCalendars.to, "to")}
                    format={dateFormat}
                    sx={{ mb: 1 }}
                    minDate={tempDateRangeCalendars.from || undefined}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => clearDate("to")}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <DateCalendar
                    value={tempDateRangeCalendars.to}
                    onChange={(date) => handleTempDateChangeCalendars(date, "to")}
                    minDate={tempDateRangeCalendars.from || undefined}
                    disableHighlightToday={true}
                    slots={{
                      day: PickersDay
                    }}
                    slotProps={{
                      day: (dayProps) => ({
                        sx: {
                          backgroundColor:
                            tempDateRangeCalendars.from &&
                            tempDateRangeCalendars.to &&
                            dayjs(dayProps.day).isBetween(
                              tempDateRangeCalendars.from,
                              tempDateRangeCalendars.to,
                              null,
                              "[]"
                            ) &&
                            !dayjs(dayProps.day).isSame(tempDateRangeCalendars.to, "day")
                              ? "lightgray"
                              : undefined
                        }
                      })
                    }}
                  />
                </Box>
              </Stack>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button onClick={handleClose} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApply}
                  disabled={
                    tempDateRangeCalendars.from && tempDateRangeCalendars.to
                      ? tempDateRangeCalendars.from.isAfter(tempDateRangeCalendars.to)
                      : false
                  }
                >
                  Apply
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

